import { useContext, useState, useEffect } from 'react';
import {
  Input,
  Checkbox,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  FormControl,
  ComboBox,
  Flex,
  ComboBoxDefaultOption,
} from '@airbus/components-react';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { useColumnContextAPI } from '../../../context/GetAllColumnContext';
import { updateView } from '../../../services/updateViewManager';
import { useAlertBar } from '../../../context/AlertBarContext';
import { t } from 'i18next';
import { useConfigContext } from '../../../context/ConfigContext';
import { postRequest } from '../../../utils';
import { useGlobalViewPermission } from './useGlobalViewPermission';
import { UserContext } from '../../../context/UserContext';
import styles from './ViewsActions.module.css';
import viewListStyles from './AddViews.module.css';
import { getApiBaseUrl } from '../../../utils/apiBase';

type ShareOption = ComboBoxDefaultOption & {
  id: number;
  role_name: string;
  firstname: string;
  lastname: string;
  user_id: number;
  role_id: number;
  label: string;
  group: string;
  name: string;
};

function AddViews(props) {
  const [selectedRoles, setSelectedRoles] = useState<ShareOption[]>([]);
  const [shareOptions, setShareOptions] = useState<ShareOption[]>([]);
  const {
    isOpenAddView,
    setIsOpenAddView,
    setAlertbar: setAlertbarParent,
    isUpdateing,
    setIsUpdateing,
    mode = 'add',
  } = props;
  const {
    STANDARD_VIEW,
    viewData,
    setStateViewManagerData,
    setSelectedViewName,
    tableDetails,
    selectedView,
  } = useColumnContextAPI();
  const apiRef = useGridApiContext();
  const { configSchema } = useConfigContext();

  const [newViewName, setNewViewName] = useState(null);
  const [viewName, setViewName] = useState('');
  const [error, setError] = useState(false);
  const [checkVal, setCheckVal] = useState(false);
  const { setAlertbar } = useAlertBar();

  const [notPermissionForGlobalActivity, setNotPermissionForGlobalActivity] = useState(false);
  const { userDetails } = useContext(UserContext);
  const [checkValGlobal, setCheckValGlobal] = useState(false);
  const [allDetails, setAllDetails] = useState<ShareOption[]>([]);
  const apiBase = getApiBaseUrl();

  useGlobalViewPermission({
    selectedView,
    userDetails,
    setIsGlobalViewActionDisable: () => {},
    setNotPermissionForGlobalActivity,
  });

  useEffect(() => {
    if (mode === 'share') {
      async function fetchUserAndRoleOptions() {
        try {
          const res = await fetch(`${apiBase}/api/getAllUsers`);
          const data = await res.json();

          const apiDetails: ShareOption[] = data?.data?.mbdcm_v_user_role_details || [];
          const currentUserId = userDetails?.userId;

          // Save full details for later role expansion
          setAllDetails(apiDetails);
          const roles: ShareOption[] = Array.from(
            new Map(
              apiDetails.map((r) => [
                r.role_id, // use role_id as key
                {
                  label: r.role_name,
                  value: `role-${r.role_id}`,
                  group: 'Roles',
                  id: r.role_id,
                  role_id: r.role_id,
                } as unknown as ShareOption,
              ]),
            ).values(),
          );
          const users: ShareOption[] = Array.from(
            new Map(
              apiDetails
                .filter((u) => u.user_id !== currentUserId)
                .map((u) => [
                  u.user_id,
                  {
                    label: `${u.firstname} ${u.lastname}`,
                    value: `user-${u.user_id}`,
                    group: 'Users',
                    id: u.user_id,
                    user_id: u.user_id,
                    role_id: u.role_id,
                  } as unknown as ShareOption,
                ]),
            ).values(),
          );
          setShareOptions([
            ...roles
              .filter((role) => role.label !== null && role.label !== 'Administrator')
              .sort((a, b) => a.label.localeCompare(b.label)),
            ...users.sort((a, b) => a.label.localeCompare(b.label)),
          ]);
        } catch (error) {
          console.error('Error fetching user/role data:', error);
        }
      }

      fetchUserAndRoleOptions();
    }
  }, [mode, userDetails?.userId]);

  const viewNameChangeHandler = (e) => {
    setViewName(e.target.value);
    const arr = viewData?.filter((item) => item.viewName === e.target.value);
    if (!arr.length && e.target.value.toLowerCase() !== STANDARD_VIEW.toLowerCase()) {
      setError(false);
      return;
    }
    setError(true);
  };

  const validateShareView = (selectedRoles, selectedView, setAlertbar) => {
    if (!selectedRoles.length) {
      setAlertbar({
        children: t('view.select_share_user_or_role'),
        severity: 'error',
      });
      return false;
    }

    if (!selectedView?.viewName) {
      setAlertbar({
        children: t('view.no_selected'),
        severity: 'error',
      });
      return false;
    }

    return true;
  };

  const handleShareView = async ({
    selectedRoles,
    selectedView,
    shareOptions,
    allDetails,
    userDetails,
    tableDetails,
    orderedFields,
    exportStateJson,
    setAlertbar,
    setSelectedRoles,
    closeModal,
    setIsUpdateing,
  }) => {
    const newViewName = selectedView.viewName;
    const shareInsertPayload: any[] = [];

    for (const entry of selectedRoles) {
      const [type] = entry.value.split('-');
      const selectedOption = shareOptions.find((opt) => opt.value === entry.value);
      if (!selectedOption) continue;

      if (type === 'user') {
        if (selectedOption.id !== userDetails?.userId) {
          shareInsertPayload.push({
            view_name: newViewName,
            user_id: selectedOption.id,
            screen_grid_id: tableDetails?.id,
            view_columns: orderedFields,
            created_by_user_id: userDetails?.userId,
            grid_filter: exportStateJson,
          });
        }
      }

      if (type === 'role') {
        const usersInRole = allDetails.filter(
          (u) => u.role_id === selectedOption.id && u.user_id !== userDetails?.userId,
        );

        usersInRole.forEach((u) => {
          shareInsertPayload.push({
            view_name: newViewName,
            user_id: u.user_id,
            role_id: u.role_id,
            screen_grid_id: tableDetails?.id,
            view_columns: orderedFields,
            created_by_user_id: userDetails?.userId,
          });
        });
      }
    }

    shareInsertPayload.splice(
      0,
      shareInsertPayload.length,
      ...Array.from(new Map(shareInsertPayload.map((item) => [item.user_id, item])).values()),
    );

    if (!shareInsertPayload.length) {
      setAlertbar({ children: t('view.no_valid_share_targets'), severity: 'error' });
      return;
    }

    setIsUpdateing(true);

    try {
      await postRequest('/api/shareView', {
        viewsToShare: shareInsertPayload,
      });

      setAlertbar({
        children: t('view.share_success'),
        severity: 'success',
      });
    } catch (err) {
      setAlertbar({
        children: t('view.errors_found', {
          msg: err?.message,
        }),
        severity: 'error',
      });
    } finally {
      setSelectedRoles([]);
      closeModal();
      setIsUpdateing(false);
    }
  };

  const validateCreateView = (viewData, viewName, orderedFields, setAlertbar) => {
    const arr = viewData?.filter((item) => item.viewName === viewName);

    if (arr.length > 0) {
      setAlertbar({
        children: t('view.name_already_present'),
        severity: 'error',
      });
      return false;
    }

    if (viewName === '') {
      setAlertbar({
        children: t('view.enter_name'),
        severity: 'error',
      });
      return false;
    }

    if (!orderedFields.length) {
      setAlertbar({
        children: t('view.select_columns'),
        severity: 'error',
      });
      return false;
    }

    return true;
  };

  const handleCreateViewSuccess = ({
    resp,
    configSchema,
    checkValGlobal,
    checkVal,
    setAlertbarParent,
    setStateViewManagerData,
    viewData,
    setSelectedViewName,
    newViewName,
    closeModal,
  }) => {
    const dataRes = resp?.[`insert_${configSchema}_mbd_view_manager`]?.returning;

    if (!dataRes?.length) {
      return;
    }

    if (!checkValGlobal) {
      setAlertbarParent({
        children: t('view.created_successfully'),
        severity: 'success',
      });
    } else {
      setAlertbarParent({
        title: t('global.view_set_global'),
        children: t('global.the_selected_view_has_been_set'),
        severity: 'success',
      });
    }

    if (!checkVal) {
      setStateViewManagerData(() => [...viewData, dataRes?.[0]]);

      setSelectedViewName(newViewName);

      closeModal();

      return;
    }

    const crntDefaultList = viewData.filter((item) => item.defaultView);

    for (const currentItem of crntDefaultList) {
      const { viewName, id } = currentItem;

      updateView(
        {
          viewColumns: viewName,
          defaultView: false,
        },
        id,
      );
    }

    const updateViewData = viewData.map((item) => ({
      ...item,
      defaultView: false,
    }));

    setStateViewManagerData(() => [...updateViewData, dataRes?.[0]]);

    setSelectedViewName(newViewName);
  };
  const handleSubmit = async () => {
    const exportState = apiRef.current.exportState();
    const exportStateJson = JSON.parse(JSON.stringify(exportState));

    const columns = exportState.columns.orderedFields;

    const orderedFields = columns.reduce((resp, field) => {
      if (
        field !== 'undoDelete' &&
        field !== 'id' &&
        (field !== tableDetails.mbdDataGrid.dataGridUniqueIdentifier || field === 'id1')
      ) {
        resp.push(field);

        const width = exportState.columns.dimensions[field]?.width;

        if (width) {
          resp.push(`width:${width}px`);
        }
      }

      return resp;
    }, []);

    if (mode === 'share') {
      const isValid = validateShareView(selectedRoles, selectedView, setAlertbar);

      if (!isValid) {
        return;
      }

      await handleShareView({
        selectedRoles,
        selectedView,
        shareOptions,
        allDetails,
        userDetails,
        tableDetails,
        orderedFields,
        exportStateJson,
        setAlertbar,
        setSelectedRoles,
        closeModal,
        setIsUpdateing,
      });

      return;
    }

    const isValid = validateCreateView(viewData, viewName, orderedFields, setAlertbar);

    if (!isValid) {
      return;
    }

    const inputParams = {
      screenGridId: tableDetails?.id,
      viewName: newViewName,
      defaultView: checkVal,
      viewColumns: orderedFields,
      gridFilter: exportStateJson,
      isGlobal: checkValGlobal,
    };

    setIsUpdateing(true);

    try {
      const resp = await postRequest('/api/views', inputParams);

      if (resp?.errors) {
        setAlertbarParent({
          children: t('view.creation_unsuccessfull', {
            msg: resp?.errors?.message,
          }),
          severity: 'error',
        });

        return;
      }

      handleCreateViewSuccess({
        resp,
        configSchema,
        checkValGlobal,
        checkVal,
        setAlertbarParent,
        setStateViewManagerData,
        viewData,
        setSelectedViewName,
        newViewName,
        closeModal,
      });
    } catch (err) {
      setAlertbarParent({
        children: t('view.creation_unsuccessfull', {
          msg: err?.message,
        }),
        severity: 'error',
      });
    } finally {
      closeModal();
      setIsUpdateing(false);
    }
  };

  const blurHandler = (e) => {
    setNewViewName(e.target.value);
  };
  const setDefault = (e) => {
    const checkValue = e.target.checked;
    setCheckVal(checkValue);
  };
  const closeModal = () => {
    setViewName('');
    setError(false);
    setIsOpenAddView(false);
  };

  return (
    <Dialog
      id='Add-view'
      aria-labelledby='adding-view'
      open={isOpenAddView}
      onClose={closeModal}
      style={{ zIndex: 1400 }}
    >
      <DialogHeader className={viewListStyles.titlecolor}>
        {mode === 'add' ? t('view.save_new_view') : t('view.view_share')}
      </DialogHeader>
      <DialogContent>
        {mode === 'add' && (
          <>
            <FormControl
              label={t('view.view_name')}
              required
              error={error}
              errorText={error && t('view.name_already')}
            >
              <Input
                id='outlined-basic'
                value={viewName}
                onChange={viewNameChangeHandler}
                onBlur={blurHandler}
              />
            </FormControl>
            <div className={styles.renameCheckBoxGroup}>
              <Checkbox label={t('view.set_default')} onChange={(e) => setDefault(e)} />
              {!notPermissionForGlobalActivity && (
                <span className={styles.checkboxPromptLeft}>
                  <Checkbox
                    label={t('global.set_global')}
                    checked={checkValGlobal}
                    onChange={(e) => setCheckValGlobal(e.target.checked)}
                  />
                </span>
              )}
            </div>
          </>
        )}
        {mode === 'share' && (
          <Flex
            className={viewListStyles.viewlist}
            spacing='2-x'
            justify='space-between'
            alignItems='center'
          >
            <ComboBox
              className='w-480'
              groupBy={(option: ShareOption) => option.group}
              multiple
              limitChips={3}
              options={shareOptions}
              value={selectedRoles}
              onChange={(val) => setSelectedRoles(val)}
              placeholder={selectedRoles.length === 0 ? t('view.select_role_user') : ''}
            />
          </Flex>
        )}
      </DialogContent>
      <DialogActions className='pt-0'>
        <Button
          onClick={() => {
            closeModal();
          }}
          variant='secondary'
          size='small'
        >
          {t('back')}
        </Button>
        <Button
          onClick={() => {
            handleSubmit();
          }}
          variant='primary'
          size='small'
          disabled={Boolean(error) || isUpdateing}
        >
          {mode === 'share' ? t('view.share') : t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddViews;
